import { useCallback, useEffect, useState } from "react";
import type {
  Actor,
  Command,
  GuildRecord,
  JobView,
  RecordValue,
} from "@guildmaster/core/platform";
import type { MemberSummary } from "@guildmaster/core/member-portal";
import { introductionSpecsByClass } from "@guildmaster/core/member-introduction";
import { ApiError, client, type PortalConfig } from "./api";
import "./member-portal.css";

type Context = GuildRecord["contextId"];
type Value<K extends RecordValue["kind"]> = Extract<RecordValue, { kind: K }>;
type Section =
  | "Overview"
  | "Characters"
  | "Availability"
  | "Wishlist"
  | "Attendance"
  | "Coaching"
  | "Guild guide";
type Save = (value: RecordValue, current?: GuildRecord) => Promise<boolean>;
const sections: Section[] = [
  "Overview",
  "Characters",
  "Availability",
  "Wishlist",
  "Attendance",
  "Coaching",
  "Guild guide",
];
const when = (value: number) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
const words = (value: string) => value.replaceAll("-", " ");
const names = Object.keys(introductionSpecsByClass).filter(
  (value) => value !== "Undecided",
);
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="member-empty">{children}</p>;
}
function Evidence({ value }: { value: string }) {
  return /^https:\/\//.test(value) ? (
    <a href={value} target="_blank" rel="noreferrer">
      Source evidence
    </a>
  ) : (
    <span>{value}</span>
  );
}

export function MemberPortal({
  actor,
  context,
  config,
  jobs,
  onSubmit,
  onAsk,
  onOpenReport,
  onDenied,
}: {
  actor: Actor;
  context: Context;
  config: PortalConfig | null;
  jobs: JobView[];
  onSubmit: (command: Command) => Promise<boolean>;
  onAsk: (prompt: string, characterId?: string, reportIds?: string[]) => void;
  onOpenReport: (id: string, revision?: number) => void;
  onDenied: (error: unknown) => void;
}) {
  const storageKey = `gm:member-character:${actor.userId}:${context}`;
  const [characterId, setCharacterId] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) ?? "";
    } catch {
      return "";
    }
  });
  const [section, setSection] = useState<Section>("Overview"),
    [summary, setSummary] = useState<MemberSummary | null>(null),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const next = await client.memberSummary(
        context,
        characterId || undefined,
      );
      setSummary(next);
      setError("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404 && characterId) {
        setCharacterId("");
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          /* Storage is optional. */
        }
        return;
      }
      if (err instanceof ApiError && [401, 403].includes(err.status)) {
        setSummary(null);
        onDenied(err);
      }
      setError(
        err instanceof Error
          ? err.message
          : "Your member information is unavailable",
      );
    }
  }, [context, characterId, storageKey, onDenied]);
  useEffect(() => {
    let active = true;
    const run = () => {
      if (active && document.visibilityState === "visible") void refresh();
    };
    run();
    const timer = window.setInterval(run, 30_000);
    window.addEventListener("focus", run);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", run);
    };
  }, [refresh]);
  const completion = jobs.map((job) => `${job.id}:${job.state}`).join("|");
  useEffect(() => {
    void refresh();
  }, [completion, refresh]);
  const pending = jobs.filter(
    (job) =>
      job.command.contextId === context &&
      job.command.type !== "analysis" &&
      ["queued", "running", "paused"].includes(job.state),
  );
  const writable = Boolean(
    config?.jobsEnabled && config.recordsEnabled && !saving && !pending.length,
  );
  const setup = writable && Boolean(config?.onboardingEnabled);
  const selected = summary?.selectedCharacter;
  const verified =
    selected?.value.kind === "character" &&
    selected.value.status === "verified";
  const save: Save = async (value, current) => {
    setSaving(true);
    try {
      return await onSubmit({
        type: "record.put",
        contextId: context,
        id: current?.id ?? crypto.randomUUID(),
        ownerId: actor.userId,
        expectedRevision: current?.revision ?? 0,
        value,
      });
    } finally {
      setSaving(false);
    }
  };
  const ask = (prompt: string, withReport = false) =>
    onAsk(
      prompt,
      verified ? selected?.id : undefined,
      withReport && summary?.latestCoaching ? [summary.latestCoaching.id] : [],
    );
  return (
    <div className="member-portal">
      <section className="member-welcome">
        <div>
          <p className="eyebrow">YOUR PLACE IN THE GUILD</p>
          <h2>Welcome, {actor.displayName}.</h2>
          <p>Your plans, your progress, your next raid.</p>
        </div>
        <label>
          Character
          <select
            value={summary?.selectedCharacter?.id ?? ""}
            onChange={(event) => {
              setSummary(null);
              setCharacterId(event.target.value);
              try {
                sessionStorage.setItem(storageKey, event.target.value);
              } catch {
                /* Storage is optional. */
              }
            }}
            disabled={!summary?.characters.length}
          >
            <option value="" disabled>
              No character linked yet
            </option>
            {summary?.characters.map(
              (record) =>
                record.value.kind === "character" && (
                  <option key={record.id} value={record.id}>
                    {record.value.name} · {record.value.status}
                  </option>
                ),
            )}
          </select>
        </label>
      </section>
      <nav className="member-sections" aria-label="Your member portal">
        {sections.map((value) => (
          <button
            key={value}
            className={section === value ? "selected" : ""}
            aria-current={section === value ? "page" : undefined}
            onClick={() => setSection(value)}
          >
            {value}
          </button>
        ))}
      </nav>
      {error && (
        <p role="alert" className="notice error">
          {error}{" "}
          <button className="text-button" onClick={() => void refresh()}>
            Try again
          </button>
        </p>
      )}
      {!config?.recordsEnabled && (
        <p className="notice">
          Member changes are being prepared for launch. Saved information
          remains available. No character setup or notifications are activated
          here.
        </p>
      )}
      {pending.length > 0 && (
        <div className="notice" role="status">
          <strong>Changes awaiting confirmation</strong>
          <p>
            Your information updates after Guildmaster completes the request.
          </p>
          {pending.map((job) => (
            <p key={job.id}>
              {job.command.type === "record.put"
                ? words(job.command.value.kind)
                : words(job.command.type)}{" "}
              · {job.state}
            </p>
          ))}
        </div>
      )}
      {!summary ? (
        !error && <p role="status">Opening your member information…</p>
      ) : (
        <>
          {section === "Overview" && (
            <>
              <div className="member-grid">
                <section className="panel member-next">
                  <p className="eyebrow">NEXT RAID</p>
                  {summary.nextRaid?.value.kind === "raid" ? (
                    <>
                      <h3>{summary.nextRaid.value.title}</h3>
                      <p>{when(summary.nextRaid.value.startsAt)}</p>
                      <p>
                        {summary.nextRaid.value.zone} ·{" "}
                        {summary.nextRaid.value.size} players
                      </p>
                      <Assignment
                        record={summary.nextAssignment}
                        raid={summary.nextRaid}
                        enabled={writable}
                        onSubmit={onSubmit}
                      />
                    </>
                  ) : (
                    <>
                      <h3>No confirmed raid yet</h3>
                      <p>
                        Raid plans appear here once officers confirm the
                        schedule.
                      </p>
                    </>
                  )}
                  <button
                    className="text-button"
                    onClick={() => setSection("Availability")}
                  >
                    Review availability
                  </button>
                </section>
                <section className="panel">
                  <p className="eyebrow">YOUR CHARACTER</p>
                  <h3>
                    {selected?.value.kind === "character"
                      ? selected.value.name
                      : "Make yourself known"}
                  </h3>
                  {selected?.value.kind === "character" ? (
                    <>
                      <p>
                        {selected.value.className ?? "Class not declared"} ·{" "}
                        {selected.value.status}
                      </p>
                      {summary.declaration?.value.kind === "declaration" && (
                        <p>
                          {summary.declaration.value.mainSpec} ·{" "}
                          {summary.declaration.value.mainOrAlt}
                        </p>
                      )}
                      {selected.value.reviewReason && (
                        <p>{selected.value.reviewReason}</p>
                      )}
                    </>
                  ) : (
                    <p>
                      Your introduction describes your plans. An officer
                      verifies your actual character separately after setup
                      opens.
                    </p>
                  )}
                  <button
                    className="text-button"
                    onClick={() => setSection("Characters")}
                  >
                    Character setup
                  </button>
                </section>
                <section className="panel">
                  <p className="eyebrow">WISHLIST</p>
                  <h3>
                    {summary.wishlist?.value.kind === "preferences"
                      ? `${summary.wishlist.value.items.length} wishes saved`
                      : "What are you working towards?"}
                  </h3>
                  <p>
                    Keep item and equipment goals together. Wishes do not
                    determine loot priority.
                  </p>
                  {summary.wishlist?.value.kind === "preferences" && (
                    <ol>
                      {summary.wishlist.value.items
                        .slice(0, 3)
                        .map((item, index) => (
                          <li key={index}>
                            {item.label ?? item.slot} ·{" "}
                            {item.use === "main"
                              ? "main spec"
                              : words(item.use)}
                          </li>
                        ))}
                    </ol>
                  )}
                  <button
                    className="text-button"
                    onClick={() => setSection("Wishlist")}
                  >
                    Your wishlist
                  </button>
                </section>
                <section className="panel">
                  <p className="eyebrow">PERSONAL COACHING</p>
                  <h3>{summary.latestCoaching?.title ?? "Room to grow"}</h3>
                  <p>
                    {summary.latestCoaching?.summary ??
                      "Your private reports appear here when reviewed and explicitly linked to your verified character."}
                  </p>
                  {summary.latestCoaching && (
                    <button
                      className="text-button"
                      onClick={() =>
                        onOpenReport(
                          summary.latestCoaching!.id,
                          summary.latestCoaching!.revision,
                        )
                      }
                    >
                      Read your report
                    </button>
                  )}
                  <button
                    className="text-button"
                    onClick={() => setSection("Coaching")}
                  >
                    Coaching and preferences
                  </button>
                </section>
              </div>
              {summary.pendingAssignments.length > 0 && (
                <section className="panel">
                  <h3>Assignments waiting for you</h3>
                  {summary.pendingAssignments.map((record) => (
                    <Assignment
                      key={record.id}
                      record={record}
                      enabled={writable}
                      onSubmit={onSubmit}
                    />
                  ))}
                </section>
              )}
              <section className="member-ask">
                <div>
                  <p className="eyebrow">ASK GUILDMASTER</p>
                  <h3>Start with what matters to you.</h3>
                  <p>
                    Private answers based on the records and reports you can
                    access.
                  </p>
                </div>
                <div className="form-actions">
                  {[
                    "Am I rostered for the next raid?",
                    "Explain my wishlist",
                    "What should I improve?",
                  ].map((prompt) => (
                    <button
                      key={prompt}
                      className="button"
                      onClick={() => ask(prompt, prompt.includes("improve"))}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}
          {section === "Characters" && (
            <>
              <section className="panel">
                <h2>Your characters</h2>
                <p>
                  Claims need officer verification. Your name and history cannot
                  be transferred to another member.
                </p>
                {summary.characters.length ? (
                  summary.characters.map(
                    (record) =>
                      record.value.kind === "character" && (
                        <article className="member-row" key={record.id}>
                          <div>
                            <strong>{record.value.name}</strong>
                            <p>
                              {record.value.className ?? "Class not declared"} ·{" "}
                              {record.value.status}
                              {record.value.reviewReason
                                ? ` · ${record.value.reviewReason}`
                                : ""}
                            </p>
                            <small>
                              Revision {record.revision} ·{" "}
                              {when(record.updatedAt)}
                            </small>
                          </div>
                          {["proposed", "rejected"].includes(
                            record.value.status,
                          ) && (
                            <ClaimForm
                              key={record.id + ":" + record.revision}
                              context={context}
                              current={record}
                              enabled={setup}
                              save={save}
                            />
                          )}
                        </article>
                      ),
                  )
                ) : (
                  <Empty>
                    No verified characters yet. Your pre-launch introduction
                    remains separate.
                  </Empty>
                )}
              </section>
              <section className="panel">
                <h3>Submit a character claim</h3>
                {!config?.onboardingEnabled && (
                  <p className="notice">
                    Character claims open after release and explicit activation.
                    No automatic activation is scheduled.
                  </p>
                )}
                <ClaimForm context={context} enabled={setup} save={save} />
              </section>
              {verified && (
                <section className="panel">
                  <h3>Main, offspec and roles</h3>
                  <DeclarationForm
                    key={selected!.id + ":" + summary.declaration?.revision}
                    character={selected!}
                    current={summary.declaration}
                    enabled={setup}
                    save={save}
                  />
                </section>
              )}
            </>
          )}
          {section === "Availability" && (
            <section className="panel">
              <h2>Your availability</h2>
              <p>
                Tell officers when you can play. Availability does not reserve a
                raid place.
              </p>
              <AvailabilityForm
                key={summary.availability?.revision ?? 0}
                current={summary.availability}
                enabled={writable}
                save={save}
              />
              {summary.assignments.length > 0 && (
                <>
                  <h3>Your assignments</h3>
                  {summary.assignments.map((record) => (
                    <Assignment
                      key={record.id}
                      record={record}
                      enabled={writable}
                      onSubmit={onSubmit}
                    />
                  ))}
                </>
              )}
            </section>
          )}
          {section === "Wishlist" && (
            <section className="panel">
              <h2>Your wishlist</h2>
              <p>
                In your preferred order, list items or equipment slots you want
                to improve. Visible to you and authorized officers. Wishes are
                separate from loot decisions and historical research.
              </p>
              {verified ? (
                <WishlistForm
                  key={selected!.id + ":" + summary.wishlist?.revision}
                  characterId={selected!.id}
                  current={summary.wishlist}
                  enabled={writable}
                  save={save}
                />
              ) : (
                <Empty>
                  Verify a character first so your wishlist belongs to the right
                  person. Visit Character setup to check your claim.
                </Empty>
              )}
            </section>
          )}
          {section === "Attendance" && (
            <section className="panel">
              <h2>Your attendance</h2>
              <p>
                Only recorded evidence is counted. Missing coverage is unknown,
                and does not establish an absence.
              </p>
              <div className="member-counts">
                {Object.entries(summary.attendanceCounts).map(
                  ([state, count]) => (
                    <div key={state}>
                      <strong>{count}</strong>
                      <span>{state}</span>
                    </div>
                  ),
                )}
              </div>
              {summary.attendance.length ? (
                summary.attendance.map(
                  (record) =>
                    record.value.kind === "attendance" && (
                      <article className="member-row" key={record.id}>
                        <div>
                          <strong>
                            {record.value.confirmed &&
                            record.value.evidence.length
                              ? record.value.state
                              : "unknown"}
                          </strong>
                          <p>
                            {when(record.value.effectiveAt)} · expectation:{" "}
                            {record.value.expectation}
                          </p>
                          <p>{record.value.notes}</p>
                          {record.value.evidence.map((evidence, index) => (
                            <p key={index}>
                              <Evidence value={evidence.reference} /> · observed{" "}
                              {when(evidence.observedAt)}
                            </p>
                          ))}
                        </div>
                      </article>
                    ),
                )
              ) : (
                <Empty>
                  No attendance evidence is recorded for this character. Raid
                  participation and coverage will appear here as they are
                  confirmed.
                </Empty>
              )}
            </section>
          )}
          {section === "Coaching" && (
            <section className="panel">
              <h2>Your coaching</h2>
              <p>
                Notifications are{" "}
                {summary.coaching.enabled ? "enabled" : "paused"}. Asking a
                question does not opt you in.
              </p>
              <button
                className="button"
                disabled={!writable}
                onClick={() =>
                  void onSubmit({
                    type: "coaching.preference.set",
                    contextId: context,
                    enabled: !summary.coaching.enabled,
                  })
                }
              >
                {summary.coaching.enabled
                  ? "Pause coaching notifications"
                  : "Opt in to private coaching notifications"}
              </button>
              {context === "forever-behind" && (
                <p className="notice">
                  Forever log analysis is waiting for a verified integration.
                  Guildmaster can explain saved information, but cannot analyse
                  new Forever raids yet.
                </p>
              )}
              {summary.personalReports.length ? (
                summary.personalReports.map((report) => (
                  <article className="member-row" key={report.id}>
                    <div>
                      <h3>{report.title}</h3>
                      <p>{report.summary}</p>
                      <p>Published {when(report.publishedAt)}</p>
                      <button
                        className="text-button"
                        onClick={() => onOpenReport(report.id, report.revision)}
                      >
                        Open evidence and report
                      </button>
                    </div>
                  </article>
                ))
              ) : (
                <Empty>
                  No reviewed personal reports are linked to this character.
                  Older reports remain in the report library until explicitly
                  associated.
                </Empty>
              )}
              <button
                className="button primary"
                onClick={() =>
                  ask(
                    "What should I improve based on my latest reviewed report?",
                    true,
                  )
                }
              >
                Ask about my progress
              </button>
            </section>
          )}
          {section === "Guild guide" && (
            <section className="panel">
              <h2>Guild guidance</h2>
              <p>
                Approved expectations, raid information and recruitment text.
              </p>
              {summary.guidance.length ? (
                summary.guidance.map(
                  (record) =>
                    record.value.kind === "reference" && (
                      <Guidance key={record.id} record={record} />
                    ),
                )
              ) : (
                <Empty>
                  No approved guidance has been published yet. Officers can
                  prepare it in Officer Desk.
                </Empty>
              )}
            </section>
          )}
          <p className="fine member-freshness">
            Updated {when(summary.refreshedAt)} · Refreshes while this page is
            visible.{" "}
            <button className="text-button" onClick={() => void refresh()}>
              Refresh now
            </button>
          </p>
        </>
      )}
    </div>
  );
}

function Assignment({
  record,
  raid,
  enabled,
  onSubmit,
}: {
  record: GuildRecord | null;
  raid?: GuildRecord | null;
  enabled: boolean;
  onSubmit: (command: Command) => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);
  if (!record || record.value.kind !== "assignment")
    return <p>You do not have a recorded assignment for this raid.</p>;
  const value = record.value,
    stale = Boolean(raid && raid.revision !== value.raidRevision);
  return (
    <div className="member-assignment">
      <strong>
        {stale
          ? "Assignment awaiting an update"
          : value.status === "confirmed"
            ? value.role === "bench"
              ? "Confirmed bench"
              : "Confirmed raid place"
            : "Proposed assignment"}
      </strong>
      <p>
        {value.spec} · {value.role} ·{" "}
        {value.acknowledgement
          ? `You ${value.acknowledgement.response}`
          : "Awaiting your response"}
      </p>
      {value.notes && <p>{value.notes}</p>}
      {value.status === "proposed" && !stale && (
        <div className="form-actions">
          {(["accepted", "declined"] as const).map((response) => (
            <button
              key={response}
              className="button"
              disabled={
                !enabled || busy || value.acknowledgement?.response === response
              }
              onClick={async () => {
                setBusy(true);
                try {
                  await onSubmit({
                    type: "assignment.respond",
                    contextId: record.contextId,
                    id: record.id,
                    expectedRevision: record.revision,
                    response,
                  });
                } finally {
                  setBusy(false);
                }
              }}
            >
              {response === "accepted"
                ? "Accept assignment"
                : "Decline assignment"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ClaimForm({
  current,
  context,
  enabled,
  save,
}: {
  current?: GuildRecord;
  context: Context;
  enabled: boolean;
  save: Save;
}) {
  const old = current?.value.kind === "character" ? current.value : undefined;
  const [name, setName] = useState(old?.name ?? ""),
    [className, setClass] = useState(old?.className ?? "Druid"),
    [realm, setRealm] = useState(old?.realm ?? ""),
    [region, setRegion] = useState(old?.region ?? "us"),
    [game, setGame] = useState<"classic" | "fresh" | "retail">(
      old?.gameVersion === "fresh" || old?.gameVersion === "retail"
        ? old.gameVersion
        : "classic",
    );
  return (
    <form
      className="member-form"
      onSubmit={async (event) => {
        event.preventDefault();
        const ok = await save(
          {
            kind: "character",
            name,
            className,
            realm: context === "forever-behind" ? null : realm,
            region: context === "forever-behind" ? null : (region as "us"),
            gameVersion: context === "forever-behind" ? "forever" : game,
            memberId: null,
            legacyCharacterId: null,
            status: "proposed",
            reviewReason: "",
            evidence: {
              source: "member-claim",
              reference: "Self-reported; awaiting officer verification",
              observedAt: Date.now(),
            },
            effectiveAt: Date.now(),
          },
          current,
        );
        if (ok && !current) setName("");
      }}
    >
      <fieldset disabled={!enabled}>
        <legend>
          {current ? "Revise and resubmit claim" : "Character details"}
        </legend>
        <div className="form-grid">
          <label>
            Exact character name
            <input
              required
              maxLength={60}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label>
            Class
            <select
              value={className}
              onChange={(event) => setClass(event.target.value)}
            >
              {names.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          {context === "years-behind" && (
            <>
              <label>
                Game
                <select
                  value={game}
                  onChange={(event) =>
                    setGame(event.target.value as typeof game)
                  }
                >
                  {["classic", "fresh", "retail"].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Realm
                <input
                  required
                  value={realm}
                  onChange={(event) => setRealm(event.target.value)}
                />
              </label>
              <label>
                Region
                <select
                  value={region}
                  onChange={(event) =>
                    setRegion(event.target.value as typeof region)
                  }
                >
                  {["us", "eu", "kr", "tw"].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
        <button className="button primary" disabled={!name.trim()}>
          Submit for verification
        </button>
      </fieldset>
    </form>
  );
}

function DeclarationForm({
  character,
  current,
  enabled,
  save,
}: {
  character: GuildRecord;
  current: GuildRecord | null;
  enabled: boolean;
  save: Save;
}) {
  const old = current?.value.kind === "declaration" ? current.value : undefined;
  const className =
    character.value.kind === "character"
      ? character.value.className
      : undefined;
  const specs: readonly string[] =
    className && Object.hasOwn(introductionSpecsByClass, className)
      ? introductionSpecsByClass[
          className as keyof typeof introductionSpecsByClass
        ]
      : [];
  const [mainOrAlt, setMain] = useState<"main" | "alt">(
      old?.mainOrAlt ?? "alt",
    ),
    [mainSpec, setSpec] = useState(old?.mainSpec ?? specs[0] ?? ""),
    [offSpec, setOff] = useState(old?.offSpec ?? ""),
    [roles, setRoles] = useState<Value<"declaration">["willingRoles"]>(
      old?.willingRoles ?? [],
    );
  return (
    <form
      className="member-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save(
          {
            kind: "declaration",
            characterId: character.id,
            mainOrAlt,
            mainSpec,
            offSpec: offSpec || null,
            willingRoles: roles,
            effectiveAt: Date.now(),
            notes: old?.notes ?? "",
          },
          current ?? undefined,
        );
      }}
    >
      <fieldset disabled={!enabled}>
        <legend>How you play</legend>
        <div className="form-grid">
          <label>
            Main or alt
            <select
              value={mainOrAlt}
              onChange={(event) =>
                setMain(event.target.value as typeof mainOrAlt)
              }
            >
              <option value="main">Main</option>
              <option value="alt">Alt</option>
            </select>
          </label>
          <label>
            Main spec
            {specs.length ? (
              <select
                value={mainSpec}
                onChange={(event) => setSpec(event.target.value)}
              >
                {specs.map((spec) => (
                  <option key={spec}>{spec}</option>
                ))}
              </select>
            ) : (
              <input
                required
                value={mainSpec}
                onChange={(event) => setSpec(event.target.value)}
              />
            )}
          </label>
          <label>
            Offspec
            <select
              value={offSpec}
              onChange={(event) => setOff(event.target.value)}
            >
              <option value="">None declared</option>
              {specs.map((spec) => (
                <option key={spec}>{spec}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="member-checks">
          {(["tank", "healer", "melee", "ranged"] as const).map((role) => (
            <label key={role}>
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={(event) =>
                  setRoles(
                    event.target.checked
                      ? [...roles, role]
                      : roles.filter((value) => value !== role),
                  )
                }
              />
              {role}
            </label>
          ))}
        </div>
        <p className="fine">
          One main per member. To switch, first mark your previous main as an
          alt.
        </p>
        <button className="button primary">Submit character preferences</button>
      </fieldset>
    </form>
  );
}

function AvailabilityForm({
  current,
  enabled,
  save,
}: {
  current: GuildRecord | null;
  enabled: boolean;
  save: Save;
}) {
  const old =
    current?.value.kind === "availability" ? current.value : undefined;
  const [timezone, setTimezone] = useState(
      old?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    ),
    [days, setDays] = useState(old?.days ?? []),
    [start, setStart] = useState(old?.startTime ?? "19:00"),
    [end, setEnd] = useState(old?.endTime ?? "23:00"),
    [notes, setNotes] = useState(old?.notes ?? "");
  return (
    <form
      className="member-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save(
          {
            kind: "availability",
            timezone,
            days,
            startTime: start,
            endTime: end,
            effectiveAt: Date.now(),
            reviewAt: Date.now() + 30 * 86400000,
            notes,
          },
          current ?? undefined,
        );
      }}
    >
      <fieldset disabled={!enabled}>
        <legend>Weekly availability</legend>
        <label>
          Timezone
          <input
            required
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            placeholder="Australia/Melbourne"
          />
        </label>
        <div className="member-checks">
          {[
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ].map((day, index) => (
            <label key={day}>
              <input
                type="checkbox"
                checked={days.includes(index)}
                onChange={(event) =>
                  setDays(
                    event.target.checked
                      ? [...days, index].sort()
                      : days.filter((value) => value !== index),
                  )
                }
              />
              {day}
            </label>
          ))}
        </div>
        <div className="form-grid">
          <label>
            From
            <input
              type="time"
              required
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>
          <label>
            Until
            <input
              type="time"
              required
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </label>
        </div>
        <label>
          Exceptions and notes
          <textarea
            maxLength={6000}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
        <p className="fine">
          Times use your selected timezone. Review your availability within 30
          days.
        </p>
        <button className="button primary">Submit availability</button>
      </fieldset>
    </form>
  );
}

function WishlistForm({
  characterId,
  current,
  enabled,
  save,
}: {
  characterId: string;
  current: GuildRecord | null;
  enabled: boolean;
  save: Save;
}) {
  const old = current?.value.kind === "preferences" ? current.value : undefined;
  const [items, setItems] = useState<Value<"preferences">["items"]>(
    old?.items ?? [],
  );
  const update = (
    index: number,
    patch: Partial<Value<"preferences">["items"][number]>,
  ) =>
    setItems(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  const move = (index: number, direction: number) => {
    const next = [...items],
      target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setItems(next);
  };
  return (
    <form
      className="member-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save(
          {
            kind: "preferences",
            characterId,
            items,
            effectiveAt: Date.now(),
            notes: old?.notes ?? "",
          },
          current ?? undefined,
        );
      }}
    >
      <fieldset disabled={!enabled}>
        <legend>Equipment wishes</legend>
        {items.map((item, index) => (
          <div className="wishlist-row" key={index}>
            <div className="wishlist-order">
              <strong>{index + 1}</strong>
              <button
                type="button"
                className="text-button"
                disabled={index === 0}
                aria-label={`Move wish ${index + 1} up`}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="text-button"
                disabled={index === items.length - 1}
                aria-label={`Move wish ${index + 1} down`}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </div>
            <div className="wishlist-fields">
              <label>
                Item or equipment goal
                <input
                  required
                  maxLength={160}
                  value={item.label ?? item.slot}
                  onChange={(event) =>
                    update(index, { label: event.target.value, itemId: null })
                  }
                  placeholder="A new main hand weapon"
                />
              </label>
              <div className="form-grid">
                <label>
                  Equipment slot
                  <input
                    required
                    maxLength={80}
                    value={item.slot}
                    onChange={(event) =>
                      update(index, { slot: event.target.value })
                    }
                    placeholder="Main hand"
                  />
                </label>
                <label>
                  Intended use
                  <select
                    value={item.use}
                    onChange={(event) =>
                      update(index, {
                        use: event.target.value as typeof item.use,
                      })
                    }
                  >
                    <option value="main">Main spec</option>
                    <option value="offspec">Offspec</option>
                    <option value="appearance">Appearance</option>
                    <option value="pass">Pass</option>
                  </select>
                </label>
              </div>
              <label>
                Notes
                <input
                  maxLength={300}
                  value={item.notes}
                  onChange={(event) =>
                    update(index, { notes: event.target.value })
                  }
                />
              </label>
            </div>
            <button
              type="button"
              className="text-button"
              aria-label={`Remove wish ${index + 1}`}
              onClick={() => setItems(items.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
        ))}
        {!items.length && (
          <Empty>
            No wishes saved yet. Start with an equipment slot if item details
            are still unknown.
          </Empty>
        )}
        <div className="form-actions">
          <button
            type="button"
            className="button"
            disabled={items.length >= 100}
            onClick={() =>
              setItems([
                ...items,
                { label: "", itemId: null, slot: "", use: "main", notes: "" },
              ])
            }
          >
            Add a wish
          </button>
          <button className="button primary">Submit wishlist</button>
        </div>
      </fieldset>
    </form>
  );
}

function Guidance({ record }: { record: GuildRecord }) {
  const [notice, setNotice] = useState("");
  if (record.value.kind !== "reference") return null;
  const value = record.value;
  return (
    <article className="member-guidance">
      <h3>{value.title}</h3>
      <p className="preserve">{value.body}</p>
      <button
        className="text-button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value.body);
            setNotice("Copied");
          } catch {
            setNotice("Select the text above to copy it");
          }
        }}
      >
        Copy approved text
      </button>
      <span role="status">{notice}</span>
      <p className="fine">
        Revision {record.revision} · {when(record.updatedAt)}
      </p>
    </article>
  );
}
